import Typography from 'components/Typography';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { FormattedMessage } from 'react-intl';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import PlanCode from 'types/PlanCode';
import PlanTypeType from 'types/PlanTypeType';

type PaymentFinePrintProps = {
  activePlanType: PlanCode;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  isCouponApplied: boolean;
  plan: PlanTypeType;
};

function PaymentFinePrintText({ activePlanType, currencyCode, coupon, isCouponApplied, plan }: PaymentFinePrintProps) {
  const languageRegionCode = useLanguageRegionCode();

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: currencyCode
  });

  const months = plan.numberOfMonths;
  const totalCost = formatter.format(Number(coupon?.discountedPricePerYear ?? plan.priceTotal));
  const baseAnnualCost = formatter.format(Number(plan.priceTotal));

  if (activePlanType === PlanCode.SevenDayTrial) {
    // always multiple months here so we don't need singular form
    return (
      <FormattedMessage
        defaultMessage="You will be charged {baseAnnualCost} when your free trial ends and it will automatically renew after {months} months unless you update your renewal settings."
        values={{ baseAnnualCost, months }}
      />
    );
  }

  if (isCouponApplied) {
    return (
      <FormattedMessage
        defaultMessage="You will be charged {cost} today and automatically renew after {months} months at {baseAnnualCost} unless you update your renewal settings."
        values={{ baseAnnualCost, cost: totalCost, months }}
      />
    );
  }

  return (
    <FormattedMessage
      defaultMessage="You will be charged {cost} today and automatically renew after {months} months unless you update your renewal settings."
      values={{ cost: totalCost, months }}
    />
  );
}

export default function PaymentFinePrint({ activePlanType, currencyCode, coupon, isCouponApplied, plan }: PaymentFinePrintProps) {
  return (
    <Typography color="gray" variant="caption">
      <PaymentFinePrintText
        activePlanType={activePlanType}
        coupon={coupon}
        currencyCode={currencyCode}
        isCouponApplied={isCouponApplied}
        plan={plan}
      />
    </Typography>
  );
}
